import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Shield, Mail, Lock, ArrowRight, Eye, EyeOff, AlertCircle, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import SEO from '@/components/SEO';

const LoginPage = () => {
  const { language } = useLanguage();
  const { login, googleLogin, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  // Redirect to dashboard if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/dashboard');
    }
  }, [authLoading, isAuthenticated, navigate]);

  const translations = {
    tr: {
      title: 'Giriş Yap',
      subtitle: 'Threat.live hesabınıza giriş yapın',
      email: 'E-posta',
      emailPlaceholder: 'ornek@sirket.com',
      password: 'Şifre',
      passwordPlaceholder: 'Şifrenizi girin',
      login: 'Giriş Yap',
      loggingIn: 'Giriş yapılıyor...',
      forgotPassword: 'Şifremi Unuttum',
      noAccount: 'Hesabınız yok mu?',
      register: 'Kayıt Ol',
      or: 'veya',
      errorInvalid: 'Hatalı e-posta veya şifre',
      errorNetwork: 'Bağlantı hatası oluştu',
      backToHome: 'Ana Sayfaya Dön',
      googleLogin: 'Google ile Giriş Yap',
      googleLoading: 'Google\'a yönlendiriliyor...',
    },
    en: {
      title: 'Sign In',
      subtitle: 'Sign in to your Threat.live account',
      email: 'Email',
      emailPlaceholder: 'example@company.com',
      password: 'Password',
      passwordPlaceholder: 'Enter your password',
      login: 'Sign In',
      loggingIn: 'Signing in...',
      forgotPassword: 'Forgot Password',
      noAccount: "Don't have an account?",
      register: 'Register',
      or: 'or',
      errorInvalid: 'Invalid email or password',
      errorNetwork: 'Connection error occurred',
      backToHome: 'Back to Home',
      googleLogin: 'Sign in with Google',
      googleLoading: 'Redirecting to Google...',
    }
  };

  const txt = translations[language] || translations.en;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const data = await login(formData.email, formData.password);
      
      if (data.status === 'success') {
        navigate('/dashboard');
      } else {
        setError(data.message || txt.errorInvalid);
      }
    } catch (err) {
      setError(err.message || txt.errorInvalid);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsGoogleLoading(true);
    setError('');
    try {
      await googleLogin();
    } catch (err) {
      setError(err.message);
      setIsGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden" data-testid="login-page">
      <SEO page="login" />
      
      {/* Background Effects */}
      <div className="absolute inset-0 grid-bg mask-radial-fade opacity-50 pointer-events-none"></div>
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-primary/5 blur-3xl pointer-events-none"></div>

      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="relative sharp p-3 bg-primary">
              <Shield className="h-7 w-7 text-primary-foreground" />
            </div>
            <span className="text-2xl font-bold tracking-tight">
              Threat<span className="text-primary">.</span>live
            </span>
          </Link>
        </div>

        <Card className="sharp border-border bg-card/50 backdrop-blur shadow-xl">
          <CardHeader className="text-center pb-2 border-b border-border bg-muted/30">
            <div className="soc-badge mx-auto mb-3">
              <span className="status-dot" />
              <span>// AUTH · SECURE</span>
            </div>
            <CardTitle className="text-xl font-mono uppercase tracking-wider">{txt.title}</CardTitle>
            <CardDescription className="text-xs">{txt.subtitle}</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Alert */}
              {error && (
                <Alert variant="destructive" className="sharp bg-red-500/10 border-red-500/30" data-testid="login-error">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription className="text-xs">{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.email}</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder={txt.emailPlaceholder}
                    value={formData.email}
                    onChange={handleChange}
                    className="pl-10 sharp text-sm"
                    required
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{txt.password}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={txt.passwordPlaceholder}
                    value={formData.password}
                    onChange={handleChange}
                    className="pl-10 pr-10 sharp text-sm"
                    required
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <div className="text-right">
                  <Link to="/forgot-password" className="text-[10px] font-mono uppercase tracking-wider text-primary hover:underline">
                    {txt.forgotPassword}
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full sharp font-mono text-xs uppercase tracking-wider bg-primary hover:bg-primary/90"
                disabled={isLoading}
                data-testid="login-submit"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {txt.loggingIn}
                  </>
                ) : (
                  <>
                    {txt.login}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border"></div>
                </div>
                <div className="relative flex justify-center text-[10px] font-mono uppercase tracking-[0.18em]">
                  <span className="bg-card px-2 text-muted-foreground">{txt.or}</span>
                </div>
              </div>

              {/* Google Login Button */}
              <Button
                type="button"
                variant="outline"
                className="w-full mt-4 gap-2 sharp font-mono text-xs uppercase tracking-wider"
                onClick={handleGoogleLogin}
                disabled={isGoogleLoading}
                data-testid="google-login-btn"
              >
                {isGoogleLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {txt.googleLoading}
                  </>
                ) : (
                  <>
                    <svg className="h-4 w-4" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    {txt.googleLogin}
                  </>
                )}
              </Button>

              <p className="mt-6 text-center text-xs text-muted-foreground">
                {txt.noAccount}{' '}
                <Link to="/register" className="text-primary hover:underline font-mono uppercase tracking-wider">
                  {txt.register}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Back to Home */}
        <p className="mt-6 text-center">
          <Link to="/" className="text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground">
            ← {txt.backToHome}
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
